import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Bell, ChevronDown, ChevronUp, MessageSquare, Send } from 'lucide-react'
import { api } from '../../services/api'

type InboxRow = {
  rootNoteId: string
  studentId: string
  firstName: string
  lastName: string
  lastMessage: string
  lastAt: string
  lastAuthor: string
  unreadCount: number
}

export default function TrainerNotesInbox() {
  const [rows, setRows] = useState<InboxRow[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [threads, setThreads] = useState<Record<string, any[]>>({})
  const [replyText, setReplyText] = useState<Record<string, string>>({})
  const [sending, setSending] = useState<string | null>(null)

  const loadInbox = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/trainer/notes/inbox')
      setRows(Array.isArray(data) ? data : [])
    } catch {
      setRows([])
      toast.error('No se pudo cargar el buzón de notas')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadInbox() }, [loadInbox])

  const openThread = async (rootNoteId: string) => {
    if (expanded === rootNoteId) {
      setExpanded(null)
      return
    }
    setExpanded(rootNoteId)
    try {
      const { data } = await api.get(`/trainer/notes/inbox/${rootNoteId}/thread`)
      setThreads((t) => ({ ...t, [rootNoteId]: Array.isArray(data) ? data : [] }))
      setRows((prev) => prev.map((r) => (
        r.rootNoteId === rootNoteId ? { ...r, unreadCount: 0 } : r
      )))
    } catch {
      toast.error('No se pudo abrir el hilo')
    }
  }

  const sendReply = async (rootNoteId: string) => {
    const text = replyText[rootNoteId]?.trim()
    if (!text) return
    setSending(rootNoteId)
    try {
      await api.post(`/trainer/notes/inbox/${rootNoteId}/reply`, { message: text })
      setReplyText((r) => ({ ...r, [rootNoteId]: '' }))
      const { data } = await api.get(`/trainer/notes/inbox/${rootNoteId}/thread`)
      setThreads((t) => ({ ...t, [rootNoteId]: Array.isArray(data) ? data : [] }))
      toast.success('Respuesta enviada (solo en la app)')
      await loadInbox()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'No se pudo enviar la respuesta')
    } finally {
      setSending(null)
    }
  }

  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-CO', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })

  const unreadTotal = rows.reduce((n, r) => n + Number(r.unreadCount || 0), 0)

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Cargando notas…</div>
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
            <Bell size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Notas de asesorados</h1>
            <p className="text-sm text-gray-500">
              {unreadTotal} sin leer · solo en la campanita (sin correo)
            </p>
          </div>
        </div>
        <Link to="/trainer/notifications" className="text-xs text-gray-500 hover:text-gray-800 underline">
          Plantillas automáticas
        </Link>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Bell size={40} className="mx-auto mb-3 opacity-30" />
          <p>Aún no hay notas de tus asesorados.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const name = `${row.firstName || ''} ${row.lastName || ''}`.trim() || 'Asesorado'
            const open = expanded === row.rootNoteId
            return (
              <div key={row.rootNoteId} className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${
                row.unreadCount > 0 ? 'border-amber-300' : 'border-gray-100'
              }`}>
                <button
                  type="button"
                  onClick={() => openThread(row.rootNoteId)}
                  className="w-full text-left p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{name}</p>
                      <p className="text-xs text-gray-400">{formatDate(row.lastAt)}</p>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{row.lastMessage}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {row.unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold min-w-[1.25rem] h-5 px-1 rounded-full flex items-center justify-center">
                          {row.unreadCount > 9 ? '9+' : row.unreadCount}
                        </span>
                      )}
                      {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    </div>
                  </div>
                </button>

                {open && (
                  <div className="border-t border-gray-100">
                    {(threads[row.rootNoteId] || []).map((msg: any) => (
                      <div
                        key={msg.id}
                        className={`p-3 border-b border-gray-50 ${
                          msg.author === 'USER' ? 'bg-primary-50' : 'bg-amber-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold ${
                            msg.author === 'USER' ? 'text-primary-700' : 'text-amber-700'
                          }`}>
                            {msg.author === 'USER' ? `👤 ${name}` : '🏋️ Tú'}
                          </span>
                          <span className="text-xs text-gray-400">{formatDate(msg.createdAt)}</span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    ))}
                    <div className="p-3 flex gap-2">
                      <input
                        className="input flex-1 text-sm"
                        placeholder="Responder (solo en la app)…"
                        value={replyText[row.rootNoteId] || ''}
                        onChange={(e) => setReplyText((r) => ({ ...r, [row.rootNoteId]: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && sendReply(row.rootNoteId)}
                      />
                      <button
                        type="button"
                        onClick={() => sendReply(row.rootNoteId)}
                        disabled={sending === row.rootNoteId || !replyText[row.rootNoteId]?.trim()}
                        className="p-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-40 transition-colors"
                        aria-label="Enviar respuesta"
                      >
                        <Send size={15} />
                      </button>
                    </div>
                    <p className="px-3 pb-3 text-[11px] text-gray-400 flex items-center gap-1">
                      <MessageSquare size={12} /> No se envía correo: el asesorado lo ve en su campanita.
                    </p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
